import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Textarea, Button } from '@components';
import { useTagManagement } from '@hooks/useTagManagement';
import { KnowledgeBase } from '../types';

interface EditKnowledgeBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description: string; tags: string }) => void;
  knowledgeBase: KnowledgeBase | null;
}

export const EditKnowledgeBaseModal: React.FC<EditKnowledgeBaseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  knowledgeBase,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
  });

  const {
    tagList,
    currentTag,
    setCurrentTag,
    handleTagKeyPress,
    removeTag,
    resetTags,
    getTagsString,
    isMaxTagsReached,
  } = useTagManagement({
    maxTags: 5,
    initialTags: knowledgeBase?.tags || '',
  });

  useEffect(() => {
    if (knowledgeBase && isOpen) {
      setFormData({
        name: knowledgeBase.name,
        description: knowledgeBase.description,
        tags: knowledgeBase.tags || '',
      });
    }
  }, [knowledgeBase, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      description: formData.description,
      tags: getTagsString(),
    });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '', tags: '' });
      resetTags();
    }
  }, [isOpen, resetTags]);

  if (!knowledgeBase) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('knowledgeBase.editKnowledgeBase')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-kb-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.knowledgeBaseNameRequired')}
            </label>
            <Input
              id="edit-kb-name"
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('knowledgeBase.enterKnowledgeBaseName')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.descriptiveNameForKnowledgeBase')}
            </p>
          </div>

          <div>
            <label
              htmlFor="edit-kb-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.description')}
            </label>
            <Textarea
              id="edit-kb-description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('knowledgeBase.enterKnowledgeBaseDescription')}
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.optionalDescriptionForKnowledgeBase')}
            </p>
          </div>

          <div>
            <label
              htmlFor="edit-kb-tags"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.tags')}{' '}
              {tagList.length > 0 && (
                <span className="text-xs text-gray-500 font-normal">({tagList.length}/5)</span>
              )}
            </label>
            <div className="space-y-2">
              <Input
                id="edit-kb-tags"
                type="text"
                value={currentTag}
                onChange={e => setCurrentTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder={
                  isMaxTagsReached
                    ? t('knowledgeBase.maximum5TagsReached')
                    : t('knowledgeBase.enterTagsAndPressEnter')
                }
                disabled={isMaxTagsReached}
                className={isMaxTagsReached ? 'opacity-50 cursor-not-allowed' : ''}
              />
              {tagList.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagList.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                      >
                        <span className="material-icons text-sm">close</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.optionalTagsForOrganization')}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('knowledgeBase.cancel')}
            </Button>
            <Button type="submit" variant="gradient-multi" className="flex items-center gap-2">
              <span className="material-icons">save</span>
              {t('knowledgeBase.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
